/**
 * 文本导出编码：UTF-8 / GBK。
 *
 * 背景：导出此前硬编码 UTF-8 + BOM，但番茄/起点等作者后台与旧版 Windows 记事本
 * 更常用 GBK；用户拿到 TXT 还得自己转码。
 *
 * 限制说明（重要）：浏览器原生不支持 GBK **编码**（`TextEncoder` 只有 utf-8），
 * 因此这里自带 GB2312 码表（`gbkTable.ts`，由脚本从 Node 的 GBK 解码器反推生成）。
 * 它覆盖 ASCII + GB2312 的 6763 个常用汉字 + 全角标点、希腊/西里尔/日文假名。
 * 码表外的字符（生僻字、emoji、部分异体字）无法用两字节 GBK 表示，
 * 会替换为 HTML 数字字符引用 `&#NNNNN;`（各平台后台均能正确还原），并计入统计。
 */
import { GB2312_CODEPOINTS, GB2312_BASE, GB2312_PER_ROW, GB2312_CODE_COUNT } from './gbkTable';

export type ExportEncoding = 'utf8' | 'gbk';

export interface EncodeResult {
  bytes: Uint8Array;
  /** 无法用目标编码表示、已替换为字符引用的字符数 */
  replacedCount: number;
  /** 被替换字符的去重样例（最多 10 个），便于提示用户 */
  replacedSamples: string[];
}

/** 码点 → GB2312 双字节序列的反查表（首次使用时惰性构建） */
let reverseMap: Map<number, [number, number]> | null = null;

function getReverseMap(): Map<number, [number, number]> {
  if (reverseMap) return reverseMap;
  const map = new Map<number, [number, number]>();
  for (let i = 0; i < GB2312_CODE_COUNT; i++) {
    const cp = GB2312_CODEPOINTS[i];
    if (!cp) continue;
    const hi = GB2312_BASE + Math.floor(i / GB2312_PER_ROW);
    const lo = GB2312_BASE + (i % GB2312_PER_ROW);
    // 同一码点可能有多个区位映射，保留首个（标准码表不会有冲突）
    if (!map.has(cp)) map.set(cp, [hi, lo]);
  }
  reverseMap = map;
  return map;
}

/** 单个码点 → GBK 字节；不可表示返回 null */
export function encodeCodePointToGbk(cp: number): [number, number] | number | null {
  if (cp < 0x80) return cp; // ASCII 单字节直通
  const pair = getReverseMap().get(cp);
  if (pair) return pair;
  // 全角空格等属于 GB2312 的常见符号已含在码表中，未命中即不可编码
  return null;
}

/** 生成 HTML 数字字符引用（码表外字符的兜底表示） */
function numericCharRef(cp: number): string {
  return `&#${cp};`;
}

/**
 * 把文本编码为 UTF-8 字节。
 * @param withBom 是否加 BOM —— Windows 记事本靠 BOM 识别 UTF-8（旧版不加会乱码）
 */
export function encodeUtf8(text: string, withBom = true): EncodeResult {
  const body = new TextEncoder().encode(text);
  if (!withBom) return { bytes: body, replacedCount: 0, replacedSamples: [] };
  const bytes = new Uint8Array(body.length + 3);
  bytes[0] = 0xef; bytes[1] = 0xbb; bytes[2] = 0xbf; // UTF-8 BOM
  bytes.set(body, 3);
  return { bytes, replacedCount: 0, replacedSamples: [] };
}

/** 把文本编码为 GBK 两字节序列；不可表示的字符降级为字符引用 */
export function encodeGbk(text: string): EncodeResult {
  const out: number[] = [];
  const replaced = new Set<string>();
  let replacedCount = 0;

  for (const ch of text) {
    const cp = ch.codePointAt(0);
    if (cp === undefined) continue;
    const encoded = encodeCodePointToGbk(cp);
    if (encoded === null) {
      replacedCount += 1;
      if (replaced.size < 10) replaced.add(ch);
      for (const byte of new TextEncoder().encode(numericCharRef(cp))) out.push(byte);
      continue;
    }
    if (typeof encoded === 'number') out.push(encoded);
    else out.push(encoded[0], encoded[1]);
  }

  return { bytes: Uint8Array.from(out), replacedCount, replacedSamples: [...replaced] };
}

/** 按指定编码编码文本 */
export function encodeText(text: string, encoding: ExportEncoding, opts?: { withBom?: boolean }): EncodeResult {
  return encoding === 'gbk' ? encodeGbk(text) : encodeUtf8(text, opts?.withBom ?? true);
}

/** 该编码对应的 MIME type（用于 Blob） */
export function mimeFor(encoding: ExportEncoding): string {
  return encoding === 'gbk' ? 'text/plain;charset=gbk' : 'text/plain;charset=utf-8';
}

/** 编码的中文标签（供 UI 展示） */
export const ENCODING_LABELS: Record<ExportEncoding, string> = {
  utf8: 'UTF-8（通用，Windows 记事本兼容）',
  gbk: 'GBK（番茄/起点作者后台、旧版记事本）',
};
